import './styles/main.css';
import { bootScreen, guideContent } from './screens/boot';
import { authScreen } from './screens/auth';
import { serverScreen } from './screens/servers';
import { characterScreen, type CharacterRow } from './screens/characters';
import { api, getCharacterId, guestLogin, refresh, setCharacter, logout, currentUser } from './api';
import { h, modal, mount, toast } from './ui/dom';

type EngineMod = typeof import('@dn/game-engine');

/** Luồng: Boot (tải thật) → Menu → Đăng nhập → Máy chủ → Nhân vật → Thế giới. */
async function main() {
  const boot = bootScreen();
  // Đăng ký service worker (PWA) sớm, không chặn luồng chính
  registerSW();

  let mod!: EngineMod; let session = false; let lastChar: CharacterRow | null = null;
  const steps = [
    { id: 'webgl', label: 'Kiểm tra WebGL', weight: 1 },
    { id: 'engine', label: 'Tải engine 3D', weight: 6 },
    { id: 'server', label: 'Kết nối máy chủ', weight: 2 },
    { id: 'session', label: 'Khôi phục phiên', weight: 1 },
    { id: 'fonts', label: 'Tải phông chữ', weight: 1 },
  ];
  const total = steps.reduce((a, s) => a + s.weight, 0); let done = 0;
  const run = async <T>(i: number, fn: () => Promise<T>) => { boot.setProgress(done / total, steps[i].label); const r = await fn(); done += steps[i].weight; boot.setProgress(done / total, steps[i].label); return r; };

  try {
    await run(0, async () => {
      const c = document.createElement('canvas'); if (!(c.getContext('webgl2') ?? c.getContext('webgl'))) throw new Error('NO_WEBGL');
    });
    mod = await run(1, () => import('@dn/game-engine'));
    await run(2, async () => { await api('/health'); });
    session = await run(3, () => refresh());
    if (session && getCharacterId()) { try { lastChar = await api<CharacterRow>(`/characters/${getCharacterId()}`); } catch { setCharacter(null); } }
    await run(4, async () => { await Promise.race([document.fonts?.ready, new Promise((r) => setTimeout(r, 2500))]); });
  } catch (e) {
    boot.destroy();
    const noGl = (e as Error).message === 'NO_WEBGL';
    mount(h('div', { class: 'screen' }, h('div', { class: 'panel fatal' }, h('h2', { style: 'color:var(--gold)' }, noGl ? 'Thiết bị không hỗ trợ 3D' : 'Không kết nối được máy chủ'),
      h('p', {}, noGl ? 'Trình duyệt hoặc thiết bị của bạn không hỗ trợ WebGL. Hãy thử Chrome, Edge, Firefox hoặc Safari bản mới, và bật tăng tốc phần cứng.' : 'Máy chủ trò chơi tạm thời không phản hồi. Vui lòng thử lại sau ít phút.'),
      h('button', { class: 'btn', onClick: () => location.reload() }, 'Thử lại'))));
    return;
  }

  const audio = new mod.AudioManager();
  const gl = mod.webglSupport();
  if (/SwiftShader|llvmpipe/i.test(gl.renderer ?? '')) toast('Cảnh báo hiệu năng', 'Thiết bị đang dựng hình bằng phần mềm – trò chơi sẽ tự dùng đồ họa Thấp.', 7000);

  for (;;) {
    const choice = await boot.showMenu({ canContinue: !!(session && lastChar), loggedIn: session, email: currentUser?.email ?? null });
    audio.unlock();
    if (choice === 'guide') { await new Promise<void>((r) => modal('Hướng dẫn', guideContent(), { onClose: r })); continue; }
    boot.destroy();
    if (choice === 'switch') { await logout().catch(() => undefined); session = false; } // đổi tài khoản
    if (!session) {
      if (choice === 'login' || choice === 'switch') await authScreen('login');
      else { try { await guestLogin(); } catch { await authScreen('register'); } } // "Bắt đầu hành trình" = chơi ngay bằng tài khoản khách
      session = true;
    }
    const server = choice === 'continue' && lastChar ? (localStorage.getItem('dn.server') ?? await serverScreen()) : await serverScreen();
    const character = choice === 'continue' && lastChar ? lastChar : await characterScreen();
    setCharacter(character.id);
    mount(h('div'));
    const { startGame } = await import('./game/GameScreen');
    await new Promise<void>((resolve) => { void startGame(mod, character, server, audio, resolve); });
    // thoát game ⇒ quay lại chọn nhân vật
    location.reload();
    return;
  }
}

function registerSW() {
  if (!('serviceWorker' in navigator) || import.meta.env.DEV) return;
  navigator.serviceWorker.register('/sw.js').then((reg) => {
    reg.addEventListener('updatefound', () => {
      const nw = reg.installing; nw?.addEventListener('statechange', () => {
        if (nw.state === 'installed' && navigator.serviceWorker.controller) {
          const bar = h('div', { class: 'panel toast', style: 'position:fixed;left:50%;bottom:20px;transform:translateX(-50%);z-index:90;display:flex;gap:10px;align-items:center' },
            'Đã có phiên bản mới.', h('button', { class: 'btn small gold', onClick: () => { nw.postMessage('skipWaiting'); } }, 'Cập nhật'));
          document.body.appendChild(bar);
        }
      });
    });
    navigator.serviceWorker.addEventListener('controllerchange', () => location.reload());
  }).catch(() => undefined);
}

void main();
